import { useState, useEffect } from 'react';
import { ScenarioCharacter } from '../../api/types/scenario';
import { api } from '../../api';
import styles from './ScenarioCharacterSelectionPopUp.module.css'; // New CSS file
import { CharacterListResponse } from '../../api/types';

type SearchTab = 'trending' | 'my-characters';

interface ScenarioCharacterSelectionPopUpProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCharacter: (character: ScenarioCharacter) => void;
  selectedCharacter: ScenarioCharacter | null;
  scenarioId: string;
}

function ScenarioCharacterSelectionPopUp({
  isOpen,
  onClose,
  onSelectCharacter,
  selectedCharacter,
  scenarioId
}: ScenarioCharacterSelectionPopUpProps): JSX.Element | null {
  const [trendingCharacters, setTrendingCharacters] = useState<CharacterListResponse[]>([]);
  const [myCharacters, setMyCharacters] = useState<CharacterListResponse[]>([]);
  const [searchResults, setSearchResults] = useState<CharacterListResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<SearchTab>('trending');
  const [searchQuery, setSearchQuery] = useState('');

  // Effect to load data when component opens
  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'trending' && trendingCharacters.length === 0) {
        loadTrendingCharacters();
      } else if (activeTab === 'my-characters' && myCharacters.length === 0) {
        loadMyCharacters();
      }
    }
  }, [isOpen, activeTab]);

  const loadTrendingCharacters = async () => {
    setIsLoading(true);
    try {
      const trending = await api.characters.filter("trending");
      setTrendingCharacters(trending);
    } catch (error) {
      console.error('Failed to load trending characters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMyCharacters = async () => {
    setIsLoading(true);
    try {
      const userCharacters = await api.characters.owned();
      setMyCharacters(userCharacters);
    } catch (error) {
      console.error('Failed to load my characters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }
    
    setIsLoading(true);
    try {
      const results = await api.characters.search(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching for characters:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab: SearchTab) => {
    setActiveTab(tab);
    setSearchQuery('');
    setSearchResults([]);
    
    // Load data if it hasn't been loaded yet
    if (tab === 'trending' && trendingCharacters.length === 0) {
      loadTrendingCharacters();
    } else if (tab === 'my-characters' && myCharacters.length === 0) {
      loadMyCharacters();
    }
  };

  // Convert CharacterListResponse to ScenarioCharacter format
  const convertToScenarioCharacter = (character: CharacterListResponse): ScenarioCharacter => {
    return {
      id: character.id,
      display_name: character.display_name,
      tagline: character.tagline,
      icon: character.icon,
      username: character.username || character.display_name.toLowerCase().replace(/\s+/g, '_'),
      is_mature: false
    };
  };

  if (!isOpen) return null;

  // Determine which characters to display
  const charactersToDisplay = searchQuery.trim() !== '' 
    ? searchResults
    : activeTab === 'trending' 
      ? trendingCharacters
      : myCharacters;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.characterSelectionModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Select Your Adventure Companion</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        {/* Search bar */}
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search for characters..."
            value={searchQuery}
            onChange={handleSearch}
            className={styles.searchInput}
          />
        </div>
        
        {/* Tabs */}
        <div className={styles.tabsContainer}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'trending' ? styles.activeTabButton : ''}`}
            onClick={() => handleTabChange('trending')}
          >
            Trending
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'my-characters' ? styles.activeTabButton : ''}`}
            onClick={() => handleTabChange('my-characters')}
          >
            My Characters
          </button>
        </div>
        
        {/* Characters list */}
        <div className={styles.charactersList}>
          {isLoading ? (
            <div className={styles.loadingCharacters}>Loading characters...</div>
          ) : charactersToDisplay.length > 0 ? (
            charactersToDisplay.map((character) => {
              const scenarioChar = convertToScenarioCharacter(character);
              return (
                <div 
                  key={character.id} 
                  className={`${styles.characterOption} ${selectedCharacter?.id === character.id ? styles.selectedCharacter : ''}`}
                  onClick={() => onSelectCharacter(scenarioChar)}
                >
                  <img 
                    src={character.icon} 
                    alt={character.display_name} 
                    className={styles.characterOptionAvatar}
                  />
                  <div className={styles.characterOptionInfo}>
                    <h4 className={styles.characterOptionName}>{character.display_name}</h4>
                    <p className={styles.characterTagline}>{character.tagline}</p>
                    {selectedCharacter?.id === character.id && (
                      <span className={styles.selectedIndicator}>Selected</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.noCharacters}>
              <p>No characters found. Try a different search or tab.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScenarioCharacterSelectionPopUp; 